import React, { useEffect, useState, useRef } from 'react';
import {
  Table, Thead, Tbody, Tr, Th, Td, Button,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  useDisclosure, Badge, Flex, Text, Box, HStack, Select, Input,
  AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader,
  AlertDialogContent, AlertDialogOverlay, useToast,
} from '@chakra-ui/react';
import { AddIcon, DownloadIcon } from '@chakra-ui/icons';
import { FiFileText } from 'react-icons/fi';
import * as XLSX from 'xlsx';
import { faenasApi, Faena, abrirPdfEnNuevaPestana } from '../../../api/serviciosApi';
import FormularioFaena from './FormularioFaena';
import ModalFaenaAvances from './ModalFaenaAvances';

const ESTATUS = ['Pendiente', 'Programada', 'En proceso', 'Finalizada', 'Cancelada'];
const PRIORIDADES = ['Baja', 'Media', 'Alta'];

const ESTATUS_COLORS: Record<string, string> = {
  Pendiente: 'yellow', Programada: 'cyan', 'En proceso': 'blue', Finalizada: 'green', Cancelada: 'red',
};
const PRIORIDAD_COLORS: Record<string, string> = { Alta: 'red', Media: 'orange', Baja: 'green' };

const TablaFaenas: React.FC = () => {
  const toast = useToast();
  const cancelRef = useRef<any>(null);

  const { isOpen: isOpenVer, onOpen: onOpenVer, onClose: onCloseVer } = useDisclosure();
  const { isOpen: isOpenEditar, onOpen: onOpenEditar, onClose: onCloseEditar } = useDisclosure();
  const { isOpen: isOpenNuevo, onOpen: onOpenNuevo, onClose: onCloseNuevo } = useDisclosure();
  const { isOpen: isOpenAvances, onOpen: onOpenAvances, onClose: onCloseAvances } = useDisclosure();
  const { isOpen: isOpenEliminar, onOpen: onOpenEliminar, onClose: onCloseEliminar } = useDisclosure();

  const [selectedItem, setSelectedItem] = useState<Faena | null>(null);
  const [data, setData] = useState<Faena[]>([]);
  const [cargando, setCargando] = useState(false);
  const [procesando, setProcesando] = useState(false);
  const [filtroEstatus, setFiltroEstatus] = useState('');
  const [filtroPrioridad, setFiltroPrioridad] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const fetchData = async () => {
    setCargando(true);
    try {
      setData(await faenasApi.list());
    } catch {
      setData([]);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const nombreSolicitante = (i: Faena) =>
    [i.solicitanteNombre, i.solicitanteApPaterno, i.solicitanteApMaterno].filter(Boolean).join(' ');

  const datosFiltrados = data.filter((item) => {
    const matchEstatus = filtroEstatus ? item.estatus === filtroEstatus : true;
    const matchPrioridad = filtroPrioridad ? item.prioridad === filtroPrioridad : true;
    const q = busqueda.toLowerCase();
    const matchBusqueda = busqueda
      ? item.folio?.toLowerCase().includes(q) ||
        item.responsable?.toLowerCase().includes(q) ||
        nombreSolicitante(item).toLowerCase().includes(q) ||
        item.localidad?.toLowerCase().includes(q)
      : true;
    return matchEstatus && matchPrioridad && matchBusqueda;
  });

  const totalPages = Math.ceil(datosFiltrados.length / itemsPerPage);
  const indexUltimo = currentPage * itemsPerPage;
  const indexPrimero = indexUltimo - itemsPerPage;
  const itemsPagina = datosFiltrados.slice(indexPrimero, indexUltimo);

  const abrirCon = async (item: Faena, open: () => void) => {
    try {
      setSelectedItem(await faenasApi.get(item.id));
    } catch {
      setSelectedItem(item);
    }
    open();
  };

  const handleGuardado = () => { onCloseNuevo(); onCloseEditar(); fetchData(); };

  const handleEliminarConfirmar = async () => {
    if (!selectedItem) return;
    setProcesando(true);
    try {
      await faenasApi.remove(selectedItem.id);
      toast({ title: 'Faena eliminada', status: 'success', duration: 3000 });
      onCloseEliminar();
      fetchData();
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message || 'No se pudo eliminar.', status: 'error', duration: 4000 });
    } finally {
      setProcesando(false);
    }
  };

  const handleExportExcel = () => {
    const filas = datosFiltrados.map((item) => ({
      Folio: item.folio,
      Fecha: item.fecha ? new Date(item.fecha).toLocaleDateString('es-MX') : '',
      'Tipo de faena': item.tipoFaena?.descripcion || '',
      Solicitante: nombreSolicitante(item),
      Prioridad: item.prioridad,
      Responsable: item.responsable,
      Localidad: item.localidad,
      Estatus: item.estatus,
    }));
    const ws = XLSX.utils.json_to_sheet(filas);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Faenas');
    XLSX.writeFile(wb, `Faenas_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const [generandoPdf, setGenerandoPdf] = useState(false);
  const handleReportePdf = async () => {
    setGenerandoPdf(true);
    try {
      const params: any = {};
      if (filtroEstatus) params.estatus = filtroEstatus;
      const blob = await faenasApi.reportePdf(params);
      abrirPdfEnNuevaPestana(blob);
    } catch {
      toast({ title: 'Error', description: 'No se pudo generar el reporte PDF.', status: 'error', duration: 4000 });
    } finally {
      setGenerandoPdf(false);
    }
  };

  const fmt = (v?: string) => (v ? new Date(v).toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—');

  return (
    <Box>
      <Flex mb={4} justify="space-between" align="center" flexWrap="wrap" gap={3}>
        <HStack spacing={3} flexWrap="wrap">
          <Input placeholder="Buscar por folio, solicitante, responsable..." value={busqueda}
            onChange={(e) => { setBusqueda(e.target.value); setCurrentPage(1); }} size="sm" w={{ base: 'full', md: '260px' }} />
          <Select placeholder="Prioridad" value={filtroPrioridad} onChange={(e) => { setFiltroPrioridad(e.target.value); setCurrentPage(1); }} size="sm" w="140px">
            {PRIORIDADES.map((p) => <option key={p} value={p}>{p}</option>)}
          </Select>
          <Select placeholder="Estatus" value={filtroEstatus} onChange={(e) => { setFiltroEstatus(e.target.value); setCurrentPage(1); }} size="sm" w="150px">
            {ESTATUS.map((s) => <option key={s} value={s}>{s}</option>)}
          </Select>
        </HStack>
        <HStack spacing={2}>
          <Button size="sm" leftIcon={<DownloadIcon />} colorScheme="green" variant="outline" onClick={handleExportExcel}>Excel</Button>
          <Button size="sm" leftIcon={<FiFileText />} bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} onClick={handleReportePdf} isLoading={generandoPdf}>Reporte PDF</Button>
          <Button size="sm" leftIcon={<AddIcon />} bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} onClick={onOpenNuevo}>Nueva faena</Button>
        </HStack>
      </Flex>

      <Box overflowX="auto" borderRadius="md" boxShadow="md" bg="white">
        <Table variant="simple" size="sm">
          <Thead bg="#5b182b">
            <Tr>
              <Th color="white" fontSize="xs">Folio</Th>
              <Th color="white" fontSize="xs">Fecha</Th>
              <Th color="white" fontSize="xs">Tipo</Th>
              <Th color="white" fontSize="xs">Solicitante</Th>
              <Th color="white" fontSize="xs">Prioridad</Th>
              <Th color="white" fontSize="xs">Estatus</Th>
              <Th color="white" fontSize="xs">Acciones</Th>
            </Tr>
          </Thead>
          <Tbody>
            {cargando ? (
              <Tr><Td colSpan={7} textAlign="center" py={8}><Text color="gray.500">Cargando...</Text></Td></Tr>
            ) : itemsPagina.length === 0 ? (
              <Tr><Td colSpan={7} textAlign="center" py={8}><Text color="gray.400">No se encontraron faenas</Text></Td></Tr>
            ) : (
              itemsPagina.map((item, idx) => (
                <Tr key={item.id} bg={idx % 2 === 0 ? 'white' : 'gray.50'} _hover={{ bg: 'red.50' }}>
                  <Td fontSize="sm">{item.folio || '—'}</Td>
                  <Td fontSize="sm">{fmt(item.fecha)}</Td>
                  <Td fontSize="sm">{item.tipoFaena?.descripcion || '—'}</Td>
                  <Td fontSize="sm">{nombreSolicitante(item) || '—'}</Td>
                  <Td><Badge colorScheme={PRIORIDAD_COLORS[item.prioridad] || 'gray'} fontSize="xs" borderRadius="full" px={2}>{item.prioridad}</Badge></Td>
                  <Td><Badge colorScheme={ESTATUS_COLORS[item.estatus] || 'gray'} fontSize="xs" borderRadius="full" px={2}>{item.estatus}</Badge></Td>
                  <Td>
                    <HStack spacing={1} flexWrap="wrap">
                      <Button size="xs" colorScheme="blue" variant="outline" onClick={() => abrirCon(item, onOpenVer)}>Ver</Button>
                      <Button size="xs" colorScheme="orange" variant="outline" onClick={() => abrirCon(item, onOpenEditar)}>Editar</Button>
                      <Button size="xs" colorScheme="teal" variant="outline" onClick={() => { setSelectedItem(item); onOpenAvances(); }}>Avances</Button>
                      <Button size="xs" colorScheme="red" variant="outline" onClick={() => { setSelectedItem(item); onOpenEliminar(); }}>Eliminar</Button>
                    </HStack>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>

      {totalPages > 1 && (
        <Flex mt={4} justify="space-between" align="center">
          <Text fontSize="sm" color="gray.500">Mostrando {indexPrimero + 1}–{Math.min(indexUltimo, datosFiltrados.length)} de {datosFiltrados.length}</Text>
          <HStack spacing={1}>
            <Button size="xs" onClick={() => setCurrentPage(1)} isDisabled={currentPage === 1}>«</Button>
            <Button size="xs" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} isDisabled={currentPage === 1}>‹</Button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              const page = Math.max(1, currentPage - 2) + i;
              if (page > totalPages) return null;
              return (
                <Button key={page} size="xs" bg={currentPage === page ? '#5b182b' : undefined} color={currentPage === page ? 'white' : undefined} onClick={() => setCurrentPage(page)}>{page}</Button>
              );
            })}
            <Button size="xs" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} isDisabled={currentPage === totalPages}>›</Button>
            <Button size="xs" onClick={() => setCurrentPage(totalPages)} isDisabled={currentPage === totalPages}>»</Button>
          </HStack>
        </Flex>
      )}

      {/* Ver */}
      <Modal isOpen={isOpenVer} onClose={onCloseVer} size="3xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color="#5b182b">Detalle de la Faena</ModalHeader>
          <ModalBody py={5}>{selectedItem && <FormularioFaena modo="view" data={selectedItem} />}</ModalBody>
          <ModalFooter><Button onClick={onCloseVer}>Cerrar</Button></ModalFooter>
        </ModalContent>
      </Modal>

      {/* Editar */}
      <Modal isOpen={isOpenEditar} onClose={onCloseEditar} size="3xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color="#5b182b">Editar Faena</ModalHeader>
          <ModalBody py={5}>{selectedItem && <FormularioFaena modo="edit" data={selectedItem} onGuardado={handleGuardado} onCancelar={onCloseEditar} />}</ModalBody>
        </ModalContent>
      </Modal>

      {/* Nuevo */}
      <Modal isOpen={isOpenNuevo} onClose={onCloseNuevo} size="3xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color="#5b182b">Nueva Faena</ModalHeader>
          <ModalBody py={5}><FormularioFaena modo="new" onGuardado={handleGuardado} onCancelar={onCloseNuevo} /></ModalBody>
        </ModalContent>
      </Modal>

      {/* Avances */}
      {selectedItem && (
        <ModalFaenaAvances isOpen={isOpenAvances} onClose={onCloseAvances} faenaId={selectedItem.id} onCambio={fetchData} />
      )}

      {/* Eliminar */}
      <AlertDialog isOpen={isOpenEliminar} leastDestructiveRef={cancelRef} onClose={onCloseEliminar}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold" color="#5b182b">Eliminar faena</AlertDialogHeader>
            <AlertDialogBody>¿Eliminar la faena <strong>{selectedItem?.folio}</strong>? Esta acción no se puede deshacer.</AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onCloseEliminar} isDisabled={procesando}>Cancelar</Button>
              <Button colorScheme="red" onClick={handleEliminarConfirmar} ml={3} isLoading={procesando} loadingText="Eliminando...">Eliminar</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default TablaFaenas;
